import { createSlice, PayloadAction, current } from '@reduxjs/toolkit';

// types
import { ComputeServiceType } from '../../utils/types';

// functions

//	--------------------------------------------------------------------------
//
//	C O N S T A N T S
//
//	--------------------------------------------------------------------------

// define the available tools.
export const WEB_TOOLS: ComputeServiceType[] = [
  {
    id: -1,
    site: {
      node: '',
      site: '',
      site_uuid: '',
    },
    resource_type: 'www',
    session_type: 'aladinlite',
    compute_service_id: '',
    service_name: 'Aladin Lite',
    service_url: '',
    is_embedded: true,
    compute_status: 'available',
  },
  {
    id: -1,
    site: {
      node: '',
      site: '',
      site_uuid: '',
    },
    resource_type: 'www',
    session_type: 'skyserver',
    compute_service_id: '',
    service_name: 'SkyServer SDSS',
    service_url: '',
    is_embedded: true,
    compute_status: 'available',
  },
];

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

export interface InteractiveSessionsState {
  //
  filterMaximised: boolean[];
  tab: number[];
  tabs: {
    service: ComputeServiceType;
    toolComponent?: any;
  }[][];

  // sessions.
  sessions: ComputeServiceType[];
  sessionsLoaded: boolean;

  // session-loading flags.
  sessionsLoading: {
    resource_type: string;
    compute_status: string;
    id?: string;
  }[];

  // aladin objects.
  aladin: any | null;
  aladinLoading: boolean;
  //
} // InteractiveSessionsState

//	--------------------------------------------------------------------------
//
//	C O N S T A N T S
//
//	--------------------------------------------------------------------------

const initialState: InteractiveSessionsState = {
  filterMaximised: [true, true, true],
  tab: [-1, -1, -1],
  tabs: [[], [], []],
  sessions: WEB_TOOLS,
  sessionsLoaded: false,
  sessionsLoading: [],
  aladin: null,
  aladinLoading: true,
};

//	--------------------------------------------------------------------------
//
//	S L I C E S
//
//	--------------------------------------------------------------------------

export const interactiveSessionsSlice = createSlice({
  name: 'interactiveSessions',
  initialState: initialState,
  reducers: {
    //
    setFilterMaximised: (
      state: InteractiveSessionsState,
      action: PayloadAction<{ filter: boolean; index: number }>,
    ) => {
      state.filterMaximised[action.payload.index] = action.payload.filter;
    },

    setTab: (
      state: InteractiveSessionsState,
      action: PayloadAction<{ tab: number; index: number }>,
    ) => {
      state.tab = [
        ...state.tab.slice(0, action.payload.index),
        ...[action.payload.tab],
        ...state.tab.slice(action.payload.index + 1),
      ];
    },

    addTab: (
      state: InteractiveSessionsState,
      action: PayloadAction<{
        service: ComputeServiceType;
        toolComponent?: any;
        index: number;
      }>,
    ) => {
      //
      // are we running embedded?
      if (action.payload.service.is_embedded === true) {
        //
        // if a web service is already open, then switch to this tab.
        let tabIndex: number = -1;
        if (action.payload.service.resource_type === 'www') {
          //
          tabIndex = state.tabs[action.payload.index].findIndex(
            (item) => item.service.session_type === action.payload.service.session_type,
          );

          state.tab = [
            ...state.tab.slice(0, action.payload.index),
            ...[tabIndex],
            ...state.tab.slice(action.payload.index + 1),
          ];
          //
        }

        // otherwise add a new tab.
        if (tabIndex === -1) {
          //
          // add the new tab to the list of tabs.
          if (action.payload.toolComponent !== undefined) {
            state.tabs[action.payload.index].push({
              service: action.payload.service,
              toolComponent: action.payload.toolComponent,
            });
          } else {
            state.tabs[action.payload.index].push({
              service: action.payload.service,
            });
          }

          state.tab = [
            ...state.tab.slice(0, action.payload.index),
            ...[state.tabs[action.payload.index].length - 1],
            ...state.tab.slice(action.payload.index + 1),
          ];
          //
        }
        //
      } else {
        //
        // open this URL in another tab.
        window.open(action.payload.service.service_url, '_blank', 'noreferrer');
        //
      }
      //
    },

    closeTab: (
      state: InteractiveSessionsState,
      action: PayloadAction<{ tab: number; index: number }>,
    ) => {
      //
      if (
        action.payload.tab > -1 &&
        action.payload.tab < state.tabs[action.payload.index].length
      ) {
        //
        const updatedTabs: {
          service: ComputeServiceType;
          toolComponent?: any;
        }[] = [
          ...state.tabs[action.payload.index].slice(0, action.payload.tab),
          ...state.tabs[action.payload.index].slice(action.payload.tab + 1),
        ];
        state.tabs = [
          ...state.tabs.slice(0, action.payload.index),
          ...[updatedTabs],
          ...state.tabs.slice(action.payload.index + 1),
        ];

        // if we'
        //
      }
      //
    },

    setSessionRefreshing: (
      state: InteractiveSessionsState,
      action: PayloadAction<{
        site_uuid: string[];
        compute_status?: string[];
        resource_type?: string[];
        refreshing: boolean;
      }>,
    ) => {
      //
      let currentState: InteractiveSessionsState = current(state);

      state.sessions = currentState.sessions.map((item) =>
        action.payload.site_uuid.indexOf(item.site.site_uuid ?? '#') > -1 &&
        ((action.payload.compute_status?.indexOf(item.compute_status) ?? -1) > -1 ||
          action.payload.compute_status === undefined) &&
        ((action.payload.resource_type?.indexOf(item.resource_type ?? '#') ?? -1) > -1 ||
          action.payload.resource_type === undefined)
          ? Object.assign({}, item, { refreshing: action.payload.refreshing !== false })
          : Object.assign({}, item),
      );
      //
    },

    setSessions: (
      state: InteractiveSessionsState,
      action: PayloadAction<ComputeServiceType[]>,
    ) => {
      state.sessions = action.payload;
    },

    addSessions: (
      state: InteractiveSessionsState,
      action: PayloadAction<ComputeServiceType[]>,
    ) => {
      state.sessions = [...state.sessions, ...action.payload];
    },

    removeSessions: (
      state: InteractiveSessionsState,
      action: PayloadAction<{
        site_uuid?: string[];
        id?: number[];
        resource_type?: string[];
        compute_status?: string[];
      }>,
    ) => {
      //
      state.sessions = state.sessions.filter(
        (item: ComputeServiceType) =>
          (((action.payload.site_uuid?.indexOf(item.site.site_uuid ?? '#') ?? -1) > -1 ||
            action.payload.site_uuid === undefined) &&
            ((action.payload.id?.indexOf(item.id ?? -1) ?? -1) > -1 ||
              action.payload.id === undefined) &&
            ((action.payload.resource_type?.indexOf(item.resource_type ?? '#') ?? -1) >
              -1 ||
              action.payload.resource_type === undefined) &&
            ((action.payload.compute_status?.indexOf(item.compute_status) ?? -1) > -1 ||
              action.payload.compute_status === undefined)) === false,
      );
      //
    },

    setSessionOverriddenStatus: (
      state: InteractiveSessionsState,
      action: PayloadAction<{
        sessionID?: number;
        computeServiceID?: string;
        status?: string;
      }>,
    ) => {
      //
      // retrieve session.
      let index: number = -1;
      if (action.payload.sessionID !== undefined) {
        index = state.sessions.findIndex(
          (item) => item.id === (action.payload.sessionID ?? -1),
        );
      }
      if (action.payload.computeServiceID !== undefined) {
        index = state.sessions.findIndex(
          (item) => item.compute_service_id === (action.payload.computeServiceID ?? -1),
        );
      }
      if (index > -1) {
        //
        let session: ComputeServiceType = state.sessions[index];
        session.overridden_status = action.payload.status;
        state.sessions = [
          ...state.sessions.slice(0, index),
          ...[session],
          ...state.sessions.slice(index + 1),
        ];
        //
      }
      //
    },

    setSessionsLoaded: (
      state: InteractiveSessionsState,
      action: PayloadAction<boolean>,
    ) => {
      state.sessionsLoaded = action.payload;
    },

    setSessionsLoading: (
      state: InteractiveSessionsState,
      action: PayloadAction<
        {
          resource_type: string;
          compute_status: string;
          id?: string;
        }[]
      >,
    ) => {
      state.sessionsLoading = action.payload;
    },

    addSessionsLoading: (
      state: InteractiveSessionsState,
      action: PayloadAction<
        {
          resource_type: string;
          compute_status: string;
          id?: string;
        }[]
      >,
    ) => {
      state.sessionsLoading = [...state.sessionsLoading, ...action.payload];
    },

    removeSessionsLoading: (
      state: InteractiveSessionsState,
      action: PayloadAction<
        {
          resource_type: string;
          compute_status: string;
          id?: string;
        }[]
      >,
    ) => {
      //
      state.sessionsLoading = state.sessionsLoading.filter(
        (item) =>
          action.payload.findIndex(
            (find) =>
              find.resource_type === item.resource_type &&
              find.compute_status === item.compute_status &&
              (find.id === item.id || find.id === undefined),
          ) === -1,
      );
      //
    },

    setAladin: (state: InteractiveSessionsState, action: PayloadAction<any>) => {
      state.aladin = action.payload;
    },

    setAladinLoading: (
      state: InteractiveSessionsState,
      action: PayloadAction<boolean>,
    ) => {
      state.aladinLoading = action.payload;
    },
    //
  } /*
  extraReducers: (builder) => {
    builder
    
      // run some code when the runService action finished, if you need to.
      .addCase(runService.fulfilled, (state: InteractiveSessionsState) => {})
  },*/,
});

// expose the interface.
export const {
  setFilterMaximised,
  setTab,
  addTab,
  closeTab,
  setSessionRefreshing,
  setSessions,
  addSessions,
  removeSessions,
  setSessionOverriddenStatus,
  setSessionsLoaded,
  setSessionsLoading,
  addSessionsLoading,
  removeSessionsLoading,
  setAladin,
  setAladinLoading,
} = interactiveSessionsSlice.actions;
export default interactiveSessionsSlice.reducer;
